import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { milestones, teamMembers } from '@/mocks/data';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于糖心视频',
    description: '糖心视频品牌介绍、发展历程与联系方式',
    url: 'https://www.tangxinshipin.com/about',
    mainEntity: {
      '@type': 'Organization',
      name: '糖心视频',
      description: '糖心视频汇聚蜜桃视频、桃子视频等甜蜜水果品牌，提供超清画质、流畅播放的在线观影体验。',
      foundingDate: '2018',
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+86-400-888-6688',
        contactType: 'customer service',
        email: 'support@tangxinshipin.com',
      },
    },
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 5000);
      return;
    }
    formData.delete('phone_alt');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, String(value));
      });

      const response = await fetch('https://readdy.ai/api/form/d9n188gbdkpk99j7gfd0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed && parsed.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <SEO
        title="关于糖心视频 - 品牌介绍 | 发展历程 | 联系我们"
        description="了解糖心视频的品牌故事、发展历程与核心价值观。糖心视频汇聚蜜桃视频、桃子视频、苹果视频、榴莲视频、芒果视频和糖果视频六大品牌，致力于提供超清画质、极速播放的在线观影体验。"
        keywords="糖心视频, 关于我们, 品牌介绍, 发展历程, 联系我们, 在线视频平台"
        canonicalUrl="https://www.tangxinshipin.com/about"
        schemaData={aboutSchema}
      />

      <Navbar />

      <main className="pt-20 md:pt-24">
        <div className="w-full px-4 md:px-6 py-4 bg-background-50">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />
        </div>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 mb-4">
                  关于糖心视频
                </h1>
                <p className="text-foreground-500 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
                  甜蜜观影，幸福每一刻
                </p>
              </div>

              <div className="relative h-56 md:h-80 rounded-2xl overflow-hidden mb-10">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20creative%20office%20team%20collaboration%20atmosphere%20with%20warm%20peach%20coral%20lighting%2C%20diverse%20team%20brainstorming%2C%20innovative%20startup%20culture%2C%20bright%20open%20workspace%20with%20natural%20light%2C%20professional%20yet%20friendly%20vibe&width=1200&height=600&seq=about-brand&orientation=landscape"
                  alt="糖心视频团队"
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div className="space-y-6 text-foreground-700">
                <p className="text-sm md:text-base leading-relaxed">
                  糖心视频创立于2018年，致力于打造中国领先的甜蜜观影平台。我们汇聚了<strong>蜜桃视频</strong>、<strong>桃子视频</strong>、<strong>苹果视频</strong>、<strong>榴莲视频</strong>、<strong>芒果视频</strong>和<strong>糖果视频</strong>六大品牌，通过超清画质和极速播放技术，为用户带来极致的在线观影体验。
                </p>
                <p className="text-sm md:text-base leading-relaxed">
                  经过多年的发展，糖心视频已经成为行业中备受用户信赖的视频平台之一。我们拥有超过2亿的累计用户，每日提供10万+部高清影片，涵盖电影、电视剧、纪录片、综艺、动漫等多种类型，满足不同用户的观影需求。
                </p>
                <p className="text-sm md:text-base leading-relaxed">
                  糖心视频始终秉持"以用户为中心"的理念，持续投入技术研发，不断优化播放体验。我们的极速播放引擎搭载全球500+CDN节点、AI画质增强算法和智能预加载技术，确保每一位用户都能享受到零缓冲、超清画质的观影体验。
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mt-12">
                <div className="bg-background-100 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-primary-100 flex items-center justify-center">
                    <i className="ri-eye-line text-primary-600 text-xl"></i>
                  </div>
                  <h4 className="font-heading text-lg font-bold text-foreground-900 mb-2">使命</h4>
                  <p className="text-foreground-500 text-xs leading-relaxed">
                    让每一个人都能轻松享受高品质的视频内容，在观影中找到快乐与感动。
                  </p>
                </div>
                <div className="bg-background-100 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-accent-100 flex items-center justify-center">
                    <i className="ri-star-line text-accent-600 text-xl"></i>
                  </div>
                  <h4 className="font-heading text-lg font-bold text-foreground-900 mb-2">愿景</h4>
                  <p className="text-foreground-500 text-xs leading-relaxed">
                    成为全球最受欢迎的甜蜜观影平台，让世界充满温暖与甜蜜的观影体验。
                  </p>
                </div>
                <div className="bg-background-100 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-secondary-100 flex items-center justify-center">
                    <i className="ri-heart-line text-secondary-600 text-xl"></i>
                  </div>
                  <h4 className="font-heading text-lg font-bold text-foreground-900 mb-2">价值观</h4>
                  <p className="text-foreground-500 text-xs leading-relaxed">
                    用户至上、创新进取、开放包容、真诚服务，用心做好每一个细节。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
                  发展历程
                </h2>
                <p className="text-foreground-500 text-sm md:text-base">
                  从初创到行业领先，每一步都坚实有力
                </p>
              </div>

              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-300 -translate-x-1/2 hidden md:block"></div>

                <div className="flex flex-col gap-6">
                  {milestones.map((item, index) => (
                    <div
                      key={item.year}
                      className={`relative flex flex-col md:flex-row gap-4 md:gap-8 ${
                        index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                      }`}
                    >
                      <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-primary-500 top-6 z-10" style={{ borderWidth: '3px', borderColor: 'oklch(var(--background-50))' }}></div>
                      <div className={`md:w-1/2 ${index % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12 md:text-left'}`}>
                        <div className="bg-background-50 rounded-xl p-4 md:p-5">
                          <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-2">
                            {item.year}
                          </span>
                          <h4 className="font-heading text-base md:text-lg font-bold text-foreground-900 mb-2">
                            {item.title}
                          </h4>
                          <p className="text-foreground-500 text-xs md:text-sm leading-relaxed">
                            {item.description}
                          </p>
                        </div>
                      </div>
                      <div className="md:w-1/2"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
                  核心团队
                </h2>
                <p className="text-foreground-500 text-sm md:text-base">
                  一群热爱视频、追求极致的伙伴
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {teamMembers.map((member) => (
                  <div
                    key={member.name}
                    className="bg-background-100 rounded-xl p-6 text-center"
                  >
                    <div className="w-20 h-20 mx-auto mb-4 rounded-full overflow-hidden">
                      <img
                        src={member.imageUrl}
                        alt={member.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <h4 className="font-heading text-lg font-bold text-foreground-900 mb-1">
                      {member.name}
                    </h4>
                    <span className="inline-block text-xs text-primary-600 font-medium mb-3">
                      {member.role}
                    </span>
                    <p className="text-foreground-500 text-xs leading-relaxed">
                      {member.bio}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
                  联系我们
                </h2>
                <p className="text-foreground-500 text-sm md:text-base">
                  有任何问题或合作意向，欢迎随时联系我们
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="flex flex-col gap-4">
                  <div className="bg-background-50 rounded-xl p-5 flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-primary-600 text-lg"></i>
                    </div>
                    <div>
                      <h4 className="font-heading text-base font-bold text-foreground-900 mb-1">电子邮箱</h4>
                      <p className="text-foreground-500 text-sm">support@tangxinshipin.com</p>
                    </div>
                  </div>
                  <div className="bg-background-50 rounded-xl p-5 flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-accent-600 text-lg"></i>
                    </div>
                    <div>
                      <h4 className="font-heading text-base font-bold text-foreground-900 mb-1">客服热线</h4>
                      <p className="text-foreground-500 text-sm">400-888-6688</p>
                      <p className="text-foreground-400 text-xs mt-0.5">服务时间：每日 9:00 - 21:00</p>
                    </div>
                  </div>
                  <div className="bg-background-50 rounded-xl p-5 flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-secondary-600 text-lg"></i>
                    </div>
                    <div>
                      <h4 className="font-heading text-base font-bold text-foreground-900 mb-1">公司地址</h4>
                      <p className="text-foreground-500 text-sm">北京市朝阳区望京科技园A座18层</p>
                    </div>
                  </div>
                </div>

                <div className="bg-background-50 rounded-xl p-6">
                  <h3 className="font-heading text-lg font-bold text-foreground-900 mb-4">
                    发送消息
                  </h3>
                  <form
                    className="flex flex-col gap-4"
                    id="contact-form"
                    data-readdy-form
                    onSubmit={handleSubmit}
                  >
                    <div>
                      <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名
                      </label>
                      <input
                        type="text"
                        name="name"
                        placeholder="请输入您的姓名"
                        className="w-full px-3 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱
                      </label>
                      <input
                        type="email"
                        name="email"
                        placeholder="请输入您的邮箱"
                        className="w-full px-3 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                        留言内容
                      </label>
                      <textarea
                        name="message"
                        rows={4}
                        maxLength={500}
                        placeholder="请输入您的留言内容（最多500字）"
                        className="w-full px-3 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                        required
                      ></textarea>
                    </div>
                    <div>
                      <input
                        type="text"
                        name="phone_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                        className="form-honeypot"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className="whitespace-nowrap cursor-pointer w-full py-3 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {formStatus === 'submitting' ? '发送中...' : '发送消息'}
                    </button>
                    {formStatus === 'success' && (
                      <p className="text-green-600 text-xs text-center">消息已成功发送！我们会尽快回复您。</p>
                    )}
                    {formStatus === 'error' && formError && (
                      <p className="text-red-500 text-xs text-center">{formError}</p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}